/**
 * The tasks screen's logic, apart from its rendering.
 *
 * What an operator may do to a task is read off the task: the server sends
 * `nextStates` from the machine, and a decision is offered only when the state
 * it would land in is among them. The mapping from verdict to target state is
 * the server's (`decisionPath` in internal/api/handlers_work.go); this file
 * repeats only the *target* of each verdict so it can look it up in
 * `nextStates`, never the transition table itself.
 */

import type { TaskResponse, TaskState } from '@/types/domain'
import type { SessionRow } from '@/terminal/wall'
export { matchesQuery } from '@/lib/search'

export type TaskDecision =
  | 'ready'
  | 'review'
  | 'approve'
  | 'rework'
  | 'cancel'
  | 'retry'
  | 'pause'
  | 'resume'

/** The state a verdict lands in — the server's mapping, repeated for lookup.
 *  `ready` from DRAFT walks through PLANNED; it is offered when PLANNED or READY
 *  is reachable. */
const DECISION_TARGET: Record<TaskDecision, TaskState[]> = {
  ready: ['PLANNED', 'READY'],
  review: ['REVIEWING'],
  approve: ['COMPLETED'],
  rework: ['REWORK_REQUIRED'],
  cancel: ['CANCELLED'],
  retry: ['READY'],
  pause: ['PAUSED'],
  resume: ['READY'],
}

/** The order decisions are offered in: the one that moves work forward first. */
const DECISION_ORDER: TaskDecision[] = [
  'ready',
  'review',
  'approve',
  'rework',
  'retry',
  'resume',
  'pause',
  'cancel',
]

/** Which verdicts a task accepts right now, from its state and next states. */
export function decisionsFor(task: Pick<TaskResponse, 'state' | 'nextStates'>): TaskDecision[] {
  const next = new Set(task.nextStates ?? [])
  return DECISION_ORDER.filter((d) => {
    switch (d) {
      case 'ready':
        return (task.state === 'DRAFT' || task.state === 'PLANNED') && DECISION_TARGET.ready.some((s) => next.has(s))
      case 'review':
        return task.state === 'RUNNING' && next.has('REVIEWING')
      case 'approve':
        return (task.state === 'REVIEWING' || task.state === 'APPROVAL_REQUIRED') && next.has('COMPLETED')
      case 'retry':
        return (task.state === 'FAILED_RETRYABLE' || task.state === 'FAILED_FINAL' || task.state === 'REWORK_REQUIRED') && next.has('READY')
      case 'resume':
        return task.state === 'PAUSED' && next.has('READY')
      default:
        return DECISION_TARGET[d].every((s) => next.has(s))
    }
  })
}

/** Whether a session may be started for the task: READY, or RUNNING with no
 *  session still running for it — the same rule the server applies. */
export function canStartSession(task: Pick<TaskResponse, 'state' | 'currentSessionId' | 'id'>, sessions: SessionRow[]): boolean {
  if (task.state === 'READY') return true
  if (task.state !== 'RUNNING') return false
  return !sessions.some((s) => (s.taskId === task.id || s.id === task.currentSessionId) && !ended(s.state))
}

function ended(state: string): boolean {
  return state === 'COMPLETED' || state === 'FAILED' || state === 'TERMINATED'
}

/** The sessions that ran for a task, newest first. */
export function sessionsForTask(task: Pick<TaskResponse, 'id' | 'currentSessionId'>, sessions: SessionRow[]): SessionRow[] {
  return sessions.filter((s) => s.taskId === task.id || s.id === task.currentSessionId)
}

/** The states in which a person owes the task a decision. */
export function taskNeedsPerson(state: TaskState): boolean {
  return (
    state === 'WAITING_FOR_HUMAN' ||
    state === 'APPROVAL_REQUIRED' ||
    state === 'AUTH_REQUIRED' ||
    state === 'REVIEWING'
  )
}

/**
 * The band a task is in: what an operator sorts and filters by.
 *
 * Twenty-three states is a machine's vocabulary, not a person's. The board has
 * always *ranked* by these five groups; naming them makes the same reading
 * available to the filter, which is the point — a chip that selects a band the
 * order does not know about would be a second, disagreeing taxonomy on one
 * screen, and the console has shipped that defect before (the status filter that
 * matched stored states while the rows showed derived activity).
 *
 * `OTHER` is everything with nobody waiting on it and nothing wrong: drafted,
 * planned, queued, held. It is a real band, not a bucket for the unrecognised —
 * a state this build has never heard of lands here and is still listed, badged
 * with whatever the server called it.
 */
export type TaskBand = 'NEEDS_PERSON' | 'READY' | 'RUNNING' | 'FAILED' | 'OTHER'

/** The bands in the order the board lists them. */
export const TASK_BANDS: TaskBand[] = ['NEEDS_PERSON', 'READY', 'RUNNING', 'FAILED', 'OTHER']

export function taskBand(state: TaskState): TaskBand {
  if (taskNeedsPerson(state)) return 'NEEDS_PERSON'
  if (state === 'READY') return 'READY'
  if (state === 'RUNNING') return 'RUNNING'
  if (state === 'FAILED_FINAL' || state === 'FAILED_RETRYABLE') return 'FAILED'
  return 'OTHER'
}

/** How many tasks are in each band, counted from the same derivation the rows
 *  and the order use, so a chip and the list under it cannot disagree. */
export function summariseTasks<T extends Pick<TaskResponse, 'state'>>(
  tasks: T[],
): Record<TaskBand, number> {
  const counts: Record<TaskBand, number> = {
    NEEDS_PERSON: 0,
    READY: 0,
    RUNNING: 0,
    FAILED: 0,
    OTHER: 0,
  }
  for (const task of tasks) counts[taskBand(task.state)]++
  return counts
}

/** Tasks ordered for a board: what needs a person, then what is ready to
 *  start, then what runs, then the rest — stable within each band. */
export function sortTasksForBoard<T extends Pick<TaskResponse, 'state' | 'priority'>>(tasks: T[]): T[] {
  const rank = (s: TaskState) => TASK_BANDS.indexOf(taskBand(s))
  return tasks
    .map((t, i) => ({ t, i, r: rank(t.state) }))
    .sort((a, b) => a.r - b.r || b.t.priority - a.t.priority || a.i - b.i)
    .map((x) => x.t)
}
